import { useState } from "react";
import { useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";

/**
 * Modal per il cambio password dell'utente loggato.
 * Richiede password corrente + nuova password (min 6 caratteri) + conferma.
 */
export function ChangePasswordDialog({ open, onOpenChange }) {
  const { authAxios } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowCurrent(false);
    setShowNext(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!current.trim()) {
      toast.error("Inserisci la password attuale");
      return;
    }
    if (next.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    if (next !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    if (current === next) {
      toast.error("La nuova password è uguale all'attuale");
      return;
    }
    setLoading(true);
    try {
      await authAxios.put("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password aggiornata con successo");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setLoading(false);
    }
  };

  const strength = (() => {
    if (!next) return null;
    if (next.length < 6) return { label: "Troppo corta", color: "text-red-400" };
    if (next.length < 8) return { label: "Debole", color: "text-amber-400" };
    const hasUpper = /[A-Z]/.test(next);
    const hasNum = /\d/.test(next);
    const hasSym = /[^a-zA-Z0-9]/.test(next);
    const score = [hasUpper, hasNum, hasSym].filter(Boolean).length;
    if (score >= 2) return { label: "Robusta", color: "text-emerald-400" };
    return { label: "Media", color: "text-yellow-400" };
  })();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <KeyRound className="w-5 h-5 text-amber-400" />
            Cambia password
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Inserisci la password attuale e la nuova. Minimo 6 caratteri.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cur-pwd" className="text-zinc-300">
              Password attuale
            </Label>
            <div className="relative">
              <Input
                id="cur-pwd"
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="current-password-input"
                disabled={loading}
                className="bg-zinc-950 border-white/10 pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-pwd" className="text-zinc-300">
              Nuova password
            </Label>
            <div className="relative">
              <Input
                id="new-pwd"
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="new-password-input"
                disabled={loading}
                className="bg-zinc-950 border-white/10 pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                tabIndex={-1}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {strength && (
              <p className={`text-xs ${strength.color}`} data-testid="password-strength">
                Sicurezza: {strength.label}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-pwd" className="text-zinc-300">
              Conferma nuova password
            </Label>
            <Input
              id="confirm-pwd"
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="confirm-password-input"
              disabled={loading}
              className="bg-zinc-950 border-white/10"
              autoComplete="new-password"
            />
            {confirm && next !== confirm && (
              <p className="text-xs text-red-400">Le password non coincidono</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={loading}
              className="text-zinc-400"
              data-testid="cancel-password-btn"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="save-password-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              Aggiorna password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
